/* eslint-disable @typescript-eslint/no-explicit-any */
import { MessageBlock } from '@deckee/deck-hand';
import React, { FC, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { telemetry } from '../../../../../services/backend';
import { getFloatPlan } from '../../../../../services/log-trips/float-plan';
import { AppState } from '../../../../../services/store';
import {
  reset,
  setFloatPlan,
} from '../../../../../services/store/modules/float-plan';
import {
  initialEndPoint,
  initialStartPoint,
  setEmergencyContacts,
  setPlanHasDiving,
  setPlanIsLoggingOn,
  setPoints,
} from '../../../../../services/store/modules/plan';
import BasicDrawerLayout from '../../../../layouts/BasicDrawerLayout/BasicDrawerLayout';
import SheetLayout from '../../../../layouts/SheetLayout/SheetLayout';
import LogoSpinner from '../../../../shared/_deprecated/components/LogoSpinner/LogoSpinner';
import useVessels from '../../../Vessels/hooks/use-vessels';
import CreateFloatPlanStep from './components/CreateFloatPlanStep';
import ShareContactStep from './components/ShareContactStep';
import FloatPlanStep from './float-plan-step';

const FloatPlanCreateContainer: FC = () => {
  const dispatch = useDispatch();
  const context = useSelector((state: AppState) => state.floatPlan);

  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const id = params.get('id');

  useEffect(() => {
    dispatch(reset());
  }, [dispatch]);

  const [isLoading, setLoading] = React.useState(true);

  const [error, setError] = React.useState<{ message: string } | null>(null);

  const { selectVessel } = useVessels({ selectFirstVessel: !id });

  const getSavedFloatPlan = React.useCallback(async () => {
    try {
      const floatPlan = await getFloatPlan(id);

      if (floatPlan?.vessel) {
        selectVessel(floatPlan.vessel);
      }

      if (floatPlan?.contacts) {
        dispatch(setEmergencyContacts(floatPlan.contacts));
      }

      dispatch(setPlanIsLoggingOn(floatPlan?.loloEnabled));
      dispatch(setPlanHasDiving(floatPlan?.isDiving));

      // if the float-plan cannot be found redirect to the create screen
      if (floatPlan == null) {
        setError({
          message: 'There was a problem loading this float plan.',
        });
      } else {
        const preparedWaypoints = floatPlan.waypoints.map((wayPoint: any) => ({
          id: wayPoint.id,
          name: wayPoint.name,
          latitude: wayPoint?.latLon?.lat,
          longitude: wayPoint?.latLon?.lon,
          ordinal: wayPoint.ordinal,
        }));

        const points = [
          {
            id: initialStartPoint.id,
            name: floatPlan?.start_waypoint_name,
            latitude: floatPlan?.start_waypoint?.lat,
            longitude: floatPlan?.start_waypoint?.lon,
            ordinal: 0,
          },
          ...preparedWaypoints,
          {
            id: initialEndPoint.id,
            name: floatPlan?.end_waypoint_name,
            latitude: floatPlan?.end_waypoint?.lat,
            longitude: floatPlan?.end_waypoint?.lon,
            ordinal: (preparedWaypoints || []).length + 2,
          },
        ];

        dispatch(setFloatPlan(floatPlan));
        dispatch(setPoints(points));
      }

      telemetry(
        telemetry.DEBUG,
        `[FloatPlanCreateContainer.getSavedFloatPlan] getting trip plan details: ${JSON.stringify(
          floatPlan,
        )}`,
      );
    } catch (err) {
      telemetry(
        telemetry.ERROR,
        `[FloatPlanCreateContainer.getSavedFloatPlan] Error retrieving ${JSON.stringify(
          err,
        )}`,
      );
    }
  }, [dispatch, id, selectVessel]);

  const initialize = React.useCallback(async () => {
    setLoading(true);
    try {
      const promises = [];

      if (id) {
        promises.push(getSavedFloatPlan());
      }

      await Promise.all(promises);
    } catch (err) {
      telemetry(
        telemetry.ERROR,
        `[FloatPlanCreateContainer.initialize] Error retrieving ${JSON.stringify(
          err,
        )}`,
      );
    } finally {
      setLoading(false);
    }
  }, [id, getSavedFloatPlan]);

  React.useEffect(() => {
    initialize();
  }, [dispatch, initialize]);

  const getStepComponent = () => {
    if (isLoading) {
      return (
        <BasicDrawerLayout heading="Your plan">
          <LogoSpinner background />
        </BasicDrawerLayout>
      );
    }

    switch (context.step) {
      case FloatPlanStep.CREATE:
        return <CreateFloatPlanStep />;
      case FloatPlanStep.SELECT_SHARE_CONTACT:
        return <ShareContactStep />;
      default:
        return <CreateFloatPlanStep />;
    }
  };

  if (error) {
    return (
      <SheetLayout
        heading={`${context.id == null ? 'Create' : 'Edit'} Float Plan`}
      >
        <MessageBlock type="error">{error?.message}</MessageBlock>
      </SheetLayout>
    );
  }

  return <>{getStepComponent()}</>;
};

FloatPlanCreateContainer.defaultProps = {
  id: undefined,
};

export default FloatPlanCreateContainer;
